// Single-step edit modal for Posted payments. Mirrors RecordPaymentModal Step 1
// structure (no wizard) but adds an Edit Reason textarea and PATCHes on submit.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPayment,
  updatePayment,
  listPaymentTypeLookups,
  listPaymentMethodLookups,
  listPaymentGatewayLookups,
  type PaymentRecordDto,
  type UpdatePaymentRequest,
} from "@/lib/invoices-payments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

export interface EditPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentRecordId: number;
  onSuccess?: () => void;
}

export function EditPaymentModal({
  open,
  onOpenChange,
  paymentRecordId,
  onSuccess,
}: EditPaymentModalProps) {
  const qc = useQueryClient();
  const paymentQ = useQuery({
    queryKey: ["payment", paymentRecordId],
    queryFn: () => getPayment(paymentRecordId),
    enabled: open,
  });
  const typesQ = useQuery({
    queryKey: ["payment-types"],
    queryFn: listPaymentTypeLookups,
  });
  const methodsQ = useQuery({
    queryKey: ["payment-methods"],
    queryFn: listPaymentMethodLookups,
  });
  const gatewaysQ = useQuery({
    queryKey: ["payment-gateways"],
    queryFn: listPaymentGatewayLookups,
  });

  const [amount, setAmount] = useState("");
  const [transactionDate, setTransactionDate] = useState("");
  const [paymentTypeCode, setPaymentTypeCode] = useState("");
  const [paymentMethodCode, setPaymentMethodCode] = useState("");
  const [paymentGatewayCode, setPaymentGatewayCode] = useState("");
  const [pymtReferenceNo, setPymtReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [editReason, setEditReason] = useState("");

  // Hydrate state from the loaded payment.
  useEffect(() => {
    const p = paymentQ.data as PaymentRecordDto | undefined;
    if (!p) return;
    setAmount(String(p.tranAmount));
    setTransactionDate(p.transactionDate.slice(0, 10));
    setPaymentTypeCode(p.paymentTypeCode);
    setPaymentMethodCode(p.paymentMethodCode ?? "");
    setPaymentGatewayCode(p.paymentGatewayCode ?? "");
    setPymtReferenceNo(p.pymtReferenceNo ?? "");
    setNotes(p.notes ?? "");
  }, [paymentQ.data]);

  const allocations = useMemo(() => {
    return paymentQ.data?.allocations ?? [];
  }, [paymentQ.data]);

  const mut = useMutation({
    mutationFn: (body: UpdatePaymentRequest) =>
      updatePayment(paymentRecordId, body),
    onSuccess: () => {
      toast.success("Payment updated");
      void qc.invalidateQueries({ queryKey: ["payment", paymentRecordId] });
      void qc.invalidateQueries({ queryKey: ["invoice-payments"] });
      void qc.invalidateQueries({ queryKey: ["payments"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error && err.message.includes("409")
          ? "Payment was modified by someone else. Refresh and retry."
          : "Failed to update payment.";
      toast.error(msg);
    },
  });

  function submit() {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Amount must be greater than 0.");
      return;
    }
    if (!editReason.trim()) {
      toast.error("Edit reason is required.");
      return;
    }
    mut.mutate({
      amount: amt,
      transactionDate,
      paymentTypeCode,
      paymentMethodCode: paymentMethodCode.trim() || null,
      paymentGatewayCode: paymentGatewayCode.trim() || null,
      pymtReferenceNo: pymtReferenceNo.trim() || null,
      notes: notes.trim() || null,
      editReason: editReason.trim(),
      // Single-invoice payment edit: send allocations=null so the backend's
      // single-invoice fast path routes the (possibly changed) full amount to
      // that invoice. Sending the loaded allocations verbatim would silently
      // under-allocate when the user raises the amount. Multi-invoice edits
      // need allocation editing (not yet shipped); send the loaded rows as-is
      // and they'll be replaced verbatim — the user must adjust the amount to
      // match the sum.
      allocations:
        paymentQ.data?.invoiceId != null
          ? null
          : allocations.length > 0
          ? allocations.map((a) => ({ invoiceId: a.invoiceId, amount: a.amount }))
          : null,
      rowVersion: paymentQ.data?.rowVersion ?? null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit payment</DialogTitle>
        </DialogHeader>

        {paymentQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="ep-amount">Amount *</Label>
                <Input
                  id="ep-amount"
                  type="number"
                  min={0}
                  step={0.01}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ep-date">Transaction date *</Label>
                <Input
                  id="ep-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ep-type">Payment type *</Label>
                <Select value={paymentTypeCode} onValueChange={setPaymentTypeCode}>
                  <SelectTrigger
                    id="ep-type"
                    aria-label="Payment type"
                    disabled={typesQ.isLoading}
                  >
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(typesQ.data ?? [])
                      .filter((t) => t.active)
                      .map((t) => (
                        <SelectItem key={t.code} value={t.code}>
                          {t.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ep-method">Payment method</Label>
                <Select
                  value={paymentMethodCode}
                  onValueChange={setPaymentMethodCode}
                >
                  <SelectTrigger
                    id="ep-method"
                    aria-label="Payment method"
                    disabled={methodsQ.isLoading}
                  >
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    {(methodsQ.data ?? [])
                      .filter((m) => m.active)
                      .map((m) => (
                        <SelectItem key={m.code} value={m.code}>
                          {m.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ep-gateway">Payment gateway</Label>
                <Select
                  value={paymentGatewayCode}
                  onValueChange={setPaymentGatewayCode}
                >
                  <SelectTrigger
                    id="ep-gateway"
                    aria-label="Payment gateway"
                    disabled={gatewaysQ.isLoading}
                  >
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    {(gatewaysQ.data ?? [])
                      .filter((g) => g.active)
                      .map((g) => (
                        <SelectItem key={g.code} value={g.code}>
                          {g.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ep-ref">Payment reference no.</Label>
                <Input
                  id="ep-ref"
                  value={pymtReferenceNo}
                  onChange={(e) => setPymtReferenceNo(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ep-notes">Notes</Label>
              <Textarea
                id="ep-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ep-reason">Edit reason *</Label>
              <Textarea
                id="ep-reason"
                rows={2}
                required
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={mut.isPending || paymentQ.isLoading}
          >
            {mut.isPending ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
